<script lang="ts">import { Button } from "@infinitro/button";
import { t, Verbiage } from "@infinitro/content";
import { Dropdown } from "@infinitro/dropdown";
import { loggedIn, ModalDialog, pushModal, stateUsername } from "@infinitro/global";
import { ChatIconComment, CloseIcon, LikeIconComment, ReplyIconComment, SmileIconComment } from "@infinitro/icons";
import { InfoCard } from "@infinitro/info-card";
import { Input } from "@infinitro/input";
import { MediaPicker } from "@infinitro/media-picker";
import { Icon } from "@infinitro/rank-badges";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import { createComment, deleteComment, getCommentReplies, getGameComments, toggleCommentLike } from "../../api/comments.service";
export let gameSlug;
export let providerSlug;
let comments = [];
let newComment = "";
let contentUri;
let sortBy = "recent";
$: sortDropdownOptions = [
	{
		value: "recent",
		label: $t(Verbiage.reviews_most_recent)
	},
	{
		value: "popular",
		label: $t(Verbiage.reviews_most_popular)
	},
	{
		value: "oldest",
		label: $t(Verbiage.reviews_oldest)
	}
];
let showReplies = {};
let replyingTo = null;
let replyingToUsername = "";
let replyText = "";
let replyContentUri;
let postingComment = false;
let postingReply = false;
let postError = null;
let likedComments = new Set();
let likingComment = null;
let showEmojiPicker = false;
let showReplyEmojiPicker = false;
let loadingReplies = {};
let loadedReplies = new Set();
let commentsLimit = 10;
let hasMore = false;
let loadingComments = false;
let openMenuCommentId = null;
let deletingComment = null;
function formatTimestamp(createdAt) {
	const date = new Date(createdAt);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMinutes = Math.floor(diffMs / (1e3 * 60));
	const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
	if (diffMinutes < 1) return "Just now";
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays === 1) return "1d ago";
	if (diffDays < 30) return `${diffDays}d ago`;
	if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
	return `${Math.floor(diffDays / 365)}y ago`;
}
function sortByNewestFirst(items) {
	return [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}
function sortByOldest(items) {
	return [...items].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}
function sortByPopular(items) {
	return [...items].sort((a, b) => b.likes - a.likes);
}
function applySorting(items) {
	switch (sortBy) {
		case "recent": return sortByNewestFirst(items);
		case "oldest": return sortByOldest(items);
		case "popular": return sortByPopular(items);
		default: return sortByNewestFirst(items);
	}
}
async function fetchComments() {
	if (!gameSlug || !providerSlug) return;
	try {
		loadingComments = true;
		const backendComments = await getGameComments(gameSlug, providerSlug, commentsLimit, "recent");
		const mappedComments = backendComments.map((comment) => ({
			id: comment.commentId,
			username: comment.username,
			rank: undefined,
			level: undefined,
			comment: comment.commentText,
			timestamp: formatTimestamp(comment.createdAt),
			likes: comment.likeCount,
			replyCount: comment.replyCount,
			replies: [],
			createdAt: comment.createdAt
		}));
		comments = applySorting(mappedComments);
		// Clear loaded replies cache to ensure fresh data is fetched when replies are toggled
		loadedReplies.clear();
		// Also reset showReplies to close all expanded reply sections
		showReplies = {};
		hasMore = backendComments.length === commentsLimit;
	} catch (error) {
		console.warn("Error fetching comments:", error);
		comments = [];
		hasMore = false;
	} finally {
		loadingComments = false;
	}
}
async function loadMoreComments() {
	if (loadingComments) return;
	try {
		loadingComments = true;
		commentsLimit += 10;
		const backendComments = await getGameComments(gameSlug, providerSlug, commentsLimit, "recent");
		const mappedComments = backendComments.map((comment) => ({
			id: comment.commentId,
			username: comment.username,
			rank: undefined,
			level: undefined,
			comment: comment.commentText,
			timestamp: formatTimestamp(comment.createdAt),
			likes: comment.likeCount,
			replyCount: comment.replyCount,
			replies: [],
			createdAt: comment.createdAt
		}));
		comments = applySorting(mappedComments);
		// Clear loaded replies cache to ensure fresh data is fetched when replies are toggled
		loadedReplies.clear();
		// Also reset showReplies to close all expanded reply sections
		showReplies = {};
		hasMore = backendComments.length === commentsLimit;
	} catch (error) {
		console.warn("Error loading more comments:", error);
	} finally {
		loadingComments = false;
	}
}
onMount(() => {
	fetchComments();
});
// Re-sort comments when sortBy changes (frontend sorting only, no refetch)
$: if (sortBy && comments.length > 0) {
	comments = applySorting([...comments]);
}
async function handlePostComment() {
	if (!$loggedIn) {
		pushModal(ModalDialog.Auth, { tab: "login" });
		return;
	}
	if (!newComment.trim() && !contentUri) return;
	try {
		postingComment = true;
		postError = null;
		// Combine text and GIF markdown if both exist
		let commentText = newComment.trim();
		if (contentUri) {
			commentText = commentText ? `${commentText} ![gif](${contentUri})` : `![gif](${contentUri})`;
		}
		const response = await createComment({
			gameSlug,
			providerSlug,
			commentText
		});
		if (response.success) {
			newComment = "";
			contentUri = undefined;
			resetTextareaHeight("#main-comment-input");
			await fetchComments();
		} else {
			postError = response.message || "Failed to post comment";
		}
	} catch (error) {
		console.warn("Error posting comment:", error);
		postError = "Failed to post comment. Please try again.";
	} finally {
		postingComment = false;
	}
}
async function toggleReplies(commentId) {
	const isExpanding = !showReplies[commentId];
	showReplies[commentId] = isExpanding;
	// Fetch replies if expanding and haven't loaded them yet
	if (isExpanding && !loadedReplies.has(commentId)) {
		try {
			loadingReplies[commentId] = true;
			const backendReplies = await getCommentReplies(commentId);
			// Update the comment in the comments array with fetched replies
			comments = comments.map((comment) => {
				if (comment.id === commentId) {
					return {
						...comment,
						replies: sortByNewestFirst(backendReplies).map((reply) => ({
							id: reply.commentId,
							username: reply.username,
							rank: undefined,
							level: undefined,
							comment: reply.commentText,
							timestamp: formatTimestamp(reply.createdAt),
							likes: reply.likeCount,
							createdAt: reply.createdAt
						}))
					};
				}
				return comment;
			});
			loadedReplies.add(commentId);
		} catch (error) {
			console.warn(`Failed to fetch replies for comment ${commentId}:`, error);
		} finally {
			loadingReplies[commentId] = false;
		}
	}
}
function getInitials(username) {
	return username.substring(0, 2).toUpperCase();
}
function startReply(commentId, username) {
	if (!$loggedIn) {
		pushModal(ModalDialog.Auth, { tab: "login" });
		return;
	}
	replyingTo = commentId;
	replyingToUsername = username;
	replyText = "";
}
function cancelReply() {
	replyingTo = null;
	replyingToUsername = "";
	replyText = "";
	replyContentUri = undefined;
	resetTextareaHeight("#reply-comment-input");
}
async function handlePostReply() {
	if (!replyText.trim() && !replyContentUri || !replyingTo) return;
	try {
		postingReply = true;
		postError = null;
		const parentCommentId = replyingTo;
		// Combine text and GIF markdown if both exist
		let commentText = replyText.trim();
		if (replyContentUri) {
			commentText = commentText ? `${commentText} ![gif](${replyContentUri})` : `![gif](${replyContentUri})`;
		}
		const response = await createComment({
			gameSlug,
			providerSlug,
			commentText,
			parentCommentId: replyingTo
		});
		if (response.success) {
			cancelReply();
			// Fetch updated replies for the parent comment
			try {
				const backendReplies = await getCommentReplies(parentCommentId);
				// Update only the specific comment's replies
				comments = comments.map((comment) => {
					if (comment.id === parentCommentId) {
						return {
							...comment,
							replyCount: backendReplies.length,
							replies: sortByNewestFirst(backendReplies).map((reply) => ({
								id: reply.commentId,
								username: reply.username,
								rank: undefined,
								level: undefined,
								comment: reply.commentText,
								timestamp: formatTimestamp(reply.createdAt),
								likes: reply.likeCount,
								createdAt: reply.createdAt
							}))
						};
					}
					return comment;
				});
				// Keep the replies expanded
				showReplies[parentCommentId] = true;
				loadedReplies.add(parentCommentId);
			} catch (error) {
				console.warn("Error refreshing replies:", error);
			}
		} else {
			postError = response.message || "Failed to post reply";
		}
	} catch (error) {
		console.warn("Error posting reply:", error);
		postError = "Failed to post reply. Please try again.";
	} finally {
		postingReply = false;
	}
}
function handleMainComposerKeydown(e) {
	const ev = e.detail;
	if (ev.key === "Enter" && !ev.shiftKey) void handlePostComment();
}
function handleMainComposerClick() {
	if (!$loggedIn) pushModal(ModalDialog.Auth, { tab: "login" });
}
function handleReplyComposerKeydown(e) {
	const ev = e.detail;
	if (ev.key === "Enter" && !ev.shiftKey) void handlePostReply();
}
async function handleLikeComment(commentId) {
	if (!$loggedIn) {
		pushModal(ModalDialog.Auth, { tab: "login" });
		return;
	}
	try {
		likingComment = commentId;
		const response = await toggleCommentLike(commentId);
		if (response.success) {
			// Update local state based on backend response
			if (response.liked) {
				likedComments.add(commentId);
			} else {
				likedComments.delete(commentId);
			}
			likedComments = likedComments;
			// Optimistically update the like count in the local comments array
			comments = comments.map((comment) => {
				if (comment.id === commentId) {
					return {
						...comment,
						likes: response.liked ? comment.likes + 1 : comment.likes - 1
					};
				}
				return comment;
			});
			// Also update like count in replies if it's a reply
			comments = comments.map((comment) => {
				if (comment.replies && comment.replies.length > 0) {
					const updatedReplies = comment.replies.map((reply) => {
						if (reply.id === commentId) {
							return {
								...reply,
								likes: response.liked ? reply.likes + 1 : reply.likes - 1
							};
						}
						return reply;
					});
					return {
						...comment,
						replies: updatedReplies
					};
				}
				return comment;
			});
		}
	} catch (error) {
		console.warn("Error toggling like:", error);
	} finally {
		likingComment = null;
	}
}
function toggleEmojiPicker() {
	showEmojiPicker = !showEmojiPicker;
	showReplyEmojiPicker = false;
}
function toggleReplyEmojiPicker() {
	showReplyEmojiPicker = !showReplyEmojiPicker;
	showEmojiPicker = false;
}
function handleEmojiSelected(event) {
	newComment += event.detail;
	showEmojiPicker = false;
}
function handleReplyEmojiSelected(event) {
	replyText += event.detail;
	showReplyEmojiPicker = false;
}
function handleGifSelected(event) {
	const gifUrl = event.detail;
	contentUri = gifUrl;
	showEmojiPicker = false;
}
function handleReplyGifSelected(event) {
	const gifUrl = event.detail;
	replyContentUri = gifUrl;
	showReplyEmojiPicker = false;
}
function parseCommentWithGifs(text) {
	const parts = [];
	const gifRegex = /!\[gif\]\((https?:\/\/[^\)]+)\)/g;
	let lastIndex = 0;
	let match;
	while ((match = gifRegex.exec(text)) !== null) {
		// Add text before the GIF
		if (match.index > lastIndex) {
			parts.push({
				type: "text",
				content: text.slice(lastIndex, match.index)
			});
		}
		// Add the GIF URL
		parts.push({
			type: "gif",
			content: match[1]
		});
		lastIndex = gifRegex.lastIndex;
	}
	// Add remaining text after last GIF
	if (lastIndex < text.length) {
		parts.push({
			type: "text",
			content: text.slice(lastIndex)
		});
	}
	// If no GIFs found, return the whole text
	if (parts.length === 0) {
		parts.push({
			type: "text",
			content: text
		});
	}
	return parts;
}
function resetTextareaHeight(selector) {
	const textarea = document.querySelector(selector);
	if (textarea) {
		textarea.style.height = "auto";
	}
}
function toggleMenu(commentId) {
	if (openMenuCommentId === commentId) {
		openMenuCommentId = null;
	} else {
		openMenuCommentId = commentId;
	}
}
function closeMenu() {
	openMenuCommentId = null;
}
async function handleDeleteComment(commentId, isReply = false, parentCommentId) {
	if (!confirm("Are you sure you want to delete this comment?")) {
		return;
	}
	try {
		deletingComment = commentId;
		closeMenu();
		const response = await deleteComment(commentId);
		if (response.success) {
			if (isReply && parentCommentId) {
				// If it's a reply, refresh the parent comment's replies
				try {
					const backendReplies = await getCommentReplies(parentCommentId);
					comments = comments.map((comment) => {
						if (comment.id === parentCommentId) {
							return {
								...comment,
								replyCount: backendReplies.length,
								replies: sortByNewestFirst(backendReplies).map((reply) => ({
									id: reply.commentId,
									username: reply.username,
									rank: undefined,
									level: undefined,
									comment: reply.commentText,
									timestamp: formatTimestamp(reply.createdAt),
									likes: reply.likeCount,
									createdAt: reply.createdAt
								}))
							};
						}
						return comment;
					});
				} catch (error) {
					console.warn("Error refreshing replies after delete:", error);
				}
			} else {
				// If it's a top-level comment, remove it from the list
				comments = comments.filter((c) => c.id !== commentId);
			}
		} else {
			alert("Failed to delete comment: " + response.message);
		}
	} catch (error) {
		console.warn("Error deleting comment:", error);
		alert("Failed to delete comment. Please try again.");
	} finally {
		deletingComment = null;
	}
}
$: charCount = newComment.length;
$: replyCharCount = replyText.length;
</script>

<div class="comments-section">
	<div class="comments-header">
		<Text type="title" titleSize="h3" customStyle="font-size: 16px;"
			>{$t(Verbiage.reviews_comments)}</Text
		>
		<Dropdown
			bind:value={sortBy}
			options={sortDropdownOptions}
			placeholder={$t(Verbiage.sort_by)}
			width="160px"
			size="small"
			customClasses="comments-sort-dropdown"
		/>
	</div>

	<InfoCard
		width="100%"
		borderRadius="12px"
		flatBackgroundColor="var(--color-background-100)"
		noBorders
	>
		<div class="comment-input-container">
			{#if $loggedIn}
				<div class="user-header">
					<Icon rank="bronze" level={1} size={24} />
					<Text type="body" bodySize="small" bodyWeight="bold">{$stateUsername || 'Guest'}</Text>
				</div>
			{/if}
			<div class="input-section-container">
				<div class="input-section">
					<div class="comment-composer-input-wrap">
						<Input
							id="main-comment-input"
							type="text"
							bind:value={newComment}
							placeholder={$t(Verbiage.reviews_leave_your_comment)}
							maxlength={200}
							size="medium"
							transparentBackground={true}
							onKeydown={handleMainComposerKeydown}
							onClick={handleMainComposerClick}
						>
							{#snippet components()}
								<div class="comment-composer-lead">
									<!-- svelte-ignore a11y-click-events-have-key-events -->
									<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
									<div
										class="emoji-icon clickable"
										on:click={() =>
											$loggedIn
												? toggleEmojiPicker()
												: pushModal(ModalDialog.Auth, { tab: 'login' })}
									>
										<SmileIconComment />
									</div>
									{#if contentUri}
										<div class="gif-preview">
											<img src={contentUri} alt="Selected GIF" class="gif-preview__image" />
											<button
												type="button"
												class="gif-preview__close"
												on:click={() => (contentUri = undefined)}
											>
												<CloseIcon />
											</button>
										</div>
									{/if}
								</div>
							{/snippet}
							{#snippet componentsEnd()}
								<div class="comment-composer-trail">
									<div class="char-count">{charCount}/200</div>
								</div>
							{/snippet}
						</Input>
					</div>
					<div class="comment-composer-submit-wrap">
						<Button
							variant="primary"
							size="small"
							width="100%"
							text={$loggedIn ? $t(Verbiage.reviews_post) : $t(Verbiage.reviews_login_to_post)}
							onClick={handlePostComment}
							isLoading={postingComment}
						/>
					</div>
				</div>
				{#if showEmojiPicker}
					<div class="emoji-picker-container">
						<MediaPicker
							on:emoji-selected={handleEmojiSelected}
							on:gif-selected={handleGifSelected}
						/>
					</div>
				{/if}
			</div>
			{#if postError}
				<div class="error-message">
					<Text type="body" bodySize="small" customStyle="color: #FF9E14">{postError}</Text>
				</div>
			{/if}
		</div>
	</InfoCard>

	<div class="comments-list">
		{#if comments.length === 0}
			<div class="no-comments">
				<Text type="body" bodySize="medium" customStyle="color: #999"
					>{$t(Verbiage.reviews_no_comments_yet)}</Text
				>
			</div>
		{:else}
			{#each comments as comment}
				<InfoCard
					width="100%"
					borderRadius="12px"
					flatBackgroundColor="var(--color-background-100)"
					noBorders
				>
					<div class="comment-container">
						<div class="comment-header">
							<div class="user-section">
								<div class="user-info-row">
									{#if comment.rank}
										<Icon rank={comment.rank} level={comment.level} size={16} />
									{/if}
									<Text type="body" bodySize="small" bodyWeight="bold">{comment.username}</Text>
									<Text type="body" bodySize="small" customStyle="color: #999"
										>{comment.timestamp}</Text
									>
								</div>
							</div>
							{#if $loggedIn && comment.username === $stateUsername}
								<div class="menu-container">
									<button class="menu-button" on:click={() => toggleMenu(comment.id)}>⋮</button>
									{#if openMenuCommentId === comment.id}
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<!-- svelte-ignore a11y-no-static-element-interactions -->
										<div class="menu-backdrop" on:click={closeMenu}></div>
										<div class="menu-dropdown">
											<InfoCard
												width="100%"
												borderRadius="8px"
												noPadding
												withoutOverflowHidden
												flatBackgroundColor="var(--color-background-100)"
												noBorders
											>
												<button
													class="menu-item delete"
													on:click={() => handleDeleteComment(comment.id, false)}
													disabled={deletingComment === comment.id}
												>
													<Text type="body" bodySize="small" customStyle="color: #FF6B6B">
														{deletingComment === comment.id ? 'Deleting...' : 'Delete'}
													</Text>
												</button>
											</InfoCard>
										</div>
									{/if}
								</div>
							{/if}
						</div>
						<div class="comment-body">
							{#each parseCommentWithGifs(comment.comment) as part}
								{#if part.type === 'text'}
									<Text type="body" bodySize="medium" customStyle="font-size: 14px;"
										>{part.content}</Text
									>
								{:else}
									<img src={part.content} alt="gif" class="comment-gif" />
								{/if}
							{/each}
						</div>
						<div class="comment-actions">
							<button
								class="like-button"
								class:active={likedComments.has(comment.id)}
								disabled={likingComment === comment.id}
								on:click={() => handleLikeComment(comment.id)}
							>
								<div class="like-icon">
									<LikeIconComment />
								</div>
								<Text type="body" bodySize="small">{comment.likes}</Text>
							</button>

							{#if comment.replyCount && comment.replyCount > 0}
								<button class="replies-button active" on:click={() => toggleReplies(comment.id)}>
									<div class="chat-icon">
										<ChatIconComment />
									</div>
									<Text type="body" bodySize="small" customStyle="color: #3EFFB4"
										>{showReplies[comment.id] ? $t(Verbiage.hide) : $t(Verbiage.show)}
										{comment.replyCount}
										{comment.replyCount === 1
											? $t(Verbiage.reviews_reply)
											: $t(Verbiage.reviews_replies)}</Text
									>
								</button>
							{/if}

							<!-- Reply button (only for base comments, not replies) -->
							<button
								class="reply-button-inline"
								on:click={() => startReply(comment.id, comment.username)}
							>
								<div class="chat-icon">
									<ReplyIconComment />
								</div>
								<Text type="body" bodySize="small">{$t(Verbiage.reviews_reply)}</Text>
							</button>
						</div>

						<!-- Reply form -->
						{#if replyingTo === comment.id}
							<div class="reply-form-container">
								<div class="reply-form-header">
									<Text type="body" bodySize="small" bodyWeight="bold">
										Your reply to {replyingToUsername}
									</Text>
									<button class="cancel-reply-button" on:click={cancelReply}>
										<Text type="body" bodySize="small">Cancel ✕</Text>
									</button>
								</div>
								<div class="input-section-container">
									<div class="input-section">
										<div class="comment-composer-input-wrap">
											<Input
												id="reply-comment-input"
												type="text"
												bind:value={replyText}
												placeholder={$t(Verbiage.reviews_leave_your_comment)}
												maxlength={200}
												size="medium"
												transparentBackground={true}
												onKeydown={handleReplyComposerKeydown}
											>
												{#snippet components()}
													<div class="comment-composer-lead">
														<!-- svelte-ignore a11y-click-events-have-key-events -->
														<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
														<div class="emoji-icon clickable" on:click={toggleReplyEmojiPicker}>
															<SmileIconComment />
														</div>
														{#if replyContentUri}
															<div class="gif-preview">
																<img
																	src={replyContentUri}
																	alt="Selected GIF"
																	class="gif-preview__image"
																/>
																<button
																	type="button"
																	class="gif-preview__close"
																	on:click={() => (replyContentUri = undefined)}
																>
																	<CloseIcon />
																</button>
															</div>
														{/if}
													</div>
												{/snippet}
												{#snippet componentsEnd()}
													<div class="comment-composer-trail">
														<div class="char-count">{replyCharCount}/200</div>
													</div>
												{/snippet}
											</Input>
										</div>
										<div class="comment-composer-submit-wrap">
											<Button
												variant="primary"
												size="small"
												width="100%"
												text={postingReply ? 'Posting...' : $t(Verbiage.reviews_reply)}
												onClick={handlePostReply}
												isLoading={postingReply}
											/>
										</div>
									</div>
									{#if showReplyEmojiPicker}
										<div class="emoji-picker-container">
											<MediaPicker
												on:emoji-selected={handleReplyEmojiSelected}
												on:gif-selected={handleReplyGifSelected}
											/>
										</div>
									{/if}
								</div>
							</div>
						{/if}

						{#if comment.replies && comment.replies.length > 0 && showReplies[comment.id]}
							<div class="replies-list">
								{#each comment.replies as reply}
									<div class="reply-item">
										<div class="comment-header">
											<div class="user-section">
												<div class="user-info-row">
													{#if reply.rank}
														<Icon rank={reply.rank} level={reply.level} size={16} />
													{/if}
													<Text type="body" bodySize="small" bodyWeight="bold"
														>{reply.username}</Text
													>
													<Text type="body" bodySize="small" customStyle="color: #999"
														>{reply.timestamp}</Text
													>
												</div>
											</div>
											{#if $loggedIn && reply.username === $stateUsername}
												<div class="menu-container">
													<button class="menu-button" on:click={() => toggleMenu(reply.id)}
														>⋮</button
													>
													{#if openMenuCommentId === reply.id}
														<!-- svelte-ignore a11y-click-events-have-key-events -->
														<!-- svelte-ignore a11y-no-static-element-interactions -->
														<div class="menu-backdrop" on:click={closeMenu}></div>
														<div class="menu-dropdown">
															<InfoCard
																width="100%"
																borderRadius="8px"
																noPadding
																withoutOverflowHidden
																flatBackgroundColor="var(--color-background-100)"
																noBorders
															>
																<button
																	class="menu-item delete"
																	on:click={() => handleDeleteComment(reply.id, true, comment.id)}
																	disabled={deletingComment === reply.id}
																>
																	<Text type="body" bodySize="small" customStyle="color: #FF6B6B">
																		{deletingComment === reply.id ? 'Deleting...' : 'Delete'}
																	</Text>
																</button>
															</InfoCard>
														</div>
													{/if}
												</div>
											{/if}
										</div>
										<div class="comment-body">
											{#each parseCommentWithGifs(reply.comment) as part}
												{#if part.type === 'text'}
													<Text type="body" bodySize="medium" customStyle="font-size: 14px;"
														>{part.content}</Text
													>
												{:else}
													<img src={part.content} alt="gif" class="comment-gif" />
												{/if}
											{/each}
										</div>
										<div class="comment-actions">
											<button
												class="like-button"
												class:active={likedComments.has(reply.id)}
												disabled={likingComment === reply.id}
												on:click={() => handleLikeComment(reply.id)}
											>
												<div class="like-icon">
													<LikeIconComment />
												</div>
												<Text type="body" bodySize="small">{reply.likes}</Text>
											</button>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					</div>
				</InfoCard>
			{/each}
		{/if}
	</div>

	{#if hasMore}
		<div class="view-more-container">
			<button class="view-more-button" on:click={loadMoreComments} disabled={loadingComments}>
				<Text type="body" bodySize="medium" bodyWeight="bold">
					{loadingComments ? 'Loading...' : $t(Verbiage.reviews_view_more_comments)}
				</Text>
			</button>
		</div>
	{/if}
</div>

<style>
	.comments-section {
		display: flex;
		flex-direction: column;
		gap: 1rem;
}

	.comments-header {
		margin-bottom: 0.5rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.comments-sort-dropdown {
		flex-shrink: 0;
		min-width: 160px;
		max-width: 160px;
	}

	.comment-input-container {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 0.75rem;
}

	.user-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
}

	.input-section-container {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 0.5rem;
		position: relative;
}

	.input-section {
		display: flex;
		width: 100%;
		align-items: center;
		gap: 0.75rem;
}

	.comment-composer-submit-wrap {
		flex-shrink: 0;
}

	.comment-composer-input-wrap {
		width: 100%;
		min-width: 0px;
		flex: 1 1 0%;
}

	.emoji-picker-container {
		width: 100%;
		max-width: 100%;
		overflow: hidden;
		position: absolute;
		bottom: 100%;
		left: 0;
		right: 0;
		margin-bottom: 8px;
		z-index: 50;
}

	.user-avatar {
		display: flex;
		height: 2.5rem;
		width: 2.5rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 9999px;
}

	.avatar-placeholder img {
		height: 100%;
		width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.comment-composer-input-wrap :global(.component-container) {
		width: 100%;
		min-width: 0px;
}

	.comment-composer-lead {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		gap: 0.5rem;
		padding-left: 1rem;
}

	.comment-composer-trail {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		padding-right: 1rem;
}

	.emoji-icon {
		flex-shrink: 0;
		width: 20px;
		height: 20px;
		opacity: 0.5;
		transition: opacity 0.2s;
}

	.emoji-icon.clickable {
		cursor: pointer;
}

	.emoji-icon.clickable:hover {
		opacity: 1;
	}

	.emoji-icon.disabled {
		cursor: not-allowed;
		opacity: 0.3;
}

	.char-count {
		flex-shrink: 0;
		font-size: 0.75rem;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(107 114 128 / var(--tw-text-opacity, 1));
}

	.error-message {
		margin-top: 0.5rem;
		text-align: center;
}

	.comments-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
}

	.no-comments {
		padding-top: 2rem;
		padding-bottom: 2rem;
		text-align: center;
}

	.comment-container {
		display: flex;
		width: 100%;
		flex-direction: column;
}

	.comment-header {
		margin-bottom: 0.75rem;
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
}

	.user-section {
		display: flex;
		align-items: flex-start;
		gap: 0.75rem;
}

	.user-info-row {
		display: flex;
		align-items: center;
		gap: 0.5rem;
}

	.divider {
		margin-left: 0.25rem;
		margin-right: 0.25rem;
		--tw-text-opacity: 1;
		color: rgb(107 114 128 / var(--tw-text-opacity, 1));
}

	.menu-button {
		cursor: pointer;
		border-style: none;
		background-color: transparent;
		padding: 0px;
		font-size: 1.25rem;
		line-height: 1.75rem;
		line-height: 1;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.menu-button:hover {
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.menu-container {
		position: relative;
}

	.menu-backdrop {
		position: fixed;
		inset: 0px;
		z-index: 40;
}

	.menu-dropdown {
		position: absolute;
		right: 0px;
		top: 100%;
		z-index: 50;
		margin-top: 0.25rem;
		overflow: visible;
		border-radius: 0.5rem;
		--tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		min-width: 120px;
}

	.menu-item {
		width: 100%;
		cursor: pointer;
		border-style: none;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
		background: transparent;
		font-family: var(--font-main);
}

	.menu-item:hover:not(:disabled) {
		background: rgba(255, 107, 107, 0.1);
	}

	.menu-item:disabled {
		cursor: not-allowed;
		opacity: 0.5;
}

	.menu-item.delete:hover:not(:disabled) {
		background: rgba(255, 107, 107, 0.15);
	}

	.comment-body {
		margin-bottom: 0.75rem;
		word-wrap: break-word;
		overflow-wrap: break-word;
		word-break: break-word;
		white-space: pre-wrap;
		display: flex;
		flex-direction: column;
		gap: 8px;
}

	.comment-gif {
		border-radius: 0.5rem;
		max-width: 300px;
		width: 100%;
		height: auto;
		-o-object-fit: contain;
		   object-fit: contain;
}

	.comment-actions {
		display: flex;
		align-items: center;
		gap: 1rem;
}

	.like-button,
	.reply-button-inline {
		display: flex;
		cursor: pointer;
		align-items: center;
		gap: 0.5rem;
		border-style: none;
		background-color: transparent;
		padding: 0px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		transition-property: opacity;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.like-button:hover,
	.reply-button-inline:hover {
		opacity: 0.7;
}

	.like-button:disabled,
	.reply-button-inline:disabled {
		cursor: not-allowed;
		opacity: 0.5;
}

	.like-icon,
	.chat-icon {
		width: 16px;
		height: 16px;
		opacity: 0.5;
		transition: all 0.2s;
	}

	.like-button:hover .like-icon,
	.like-button.active .like-icon,
	.replies-button:hover .chat-icon,
	.replies-button.active .chat-icon {
		opacity: 1;
		filter: brightness(0) saturate(100%) invert(84%) sepia(41%) saturate(1734%) hue-rotate(93deg)
			brightness(102%) contrast(104%);
	}

	.reply-icon {
		font-size: 1rem;
		line-height: 1.5rem;
}

	.replies-button {
		display: flex;
		cursor: pointer;
		align-items: center;
		gap: 0.5rem;
		border-style: none;
		background-color: transparent;
		padding: 0px;
		transition-property: opacity;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.replies-button:hover {
		opacity: 0.7;
}

	.replies-list {
		margin-top: 1rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		border-left-width: 2px;
		padding-left: 1rem;
		border-color: #3e4b78;
}

	.reply-item {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
}

	.reply-form-container {
		margin-top: 1rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		border-radius: 0.5rem;
		padding: 0.75rem;
		background: rgba(62, 75, 120, 0.2);
		border: 1px solid #3e4b78;
}

	.reply-form-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.cancel-reply-button {
		cursor: pointer;
		border-style: none;
		background-color: transparent;
		padding: 0px;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
}

	.cancel-reply-button:hover {
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.view-more-container {
		margin-top: 0.5rem;
		display: flex;
		justify-content: center;
}

	.view-more-button {
		cursor: pointer;
		border-radius: 9999px;
		padding-left: 2rem;
		padding-right: 2rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
		background: linear-gradient(
			180deg,
			var(--color-background-700) 0%,
			var(--color-background-900) 100%
		);
		color: white;
		font-family: var(--font-main);
}

	.view-more-button:hover {
		opacity: 0.9;
}

	.gif-preview {
		position: relative;
		margin-right: -2rem;
		padding-left: 0.5rem;
}

	.gif-preview__image {
		width: 60%;
		max-width: 100px;
		border-radius: 0.5rem;
}

	.gif-preview__close {
		pointer-events: none;
		position: absolute;
		top: -8px;
		right: 0.75rem;
		border-radius: 9999px;
		border-style: none;
		background-color: rgb(31 41 55 / var(--tw-bg-opacity, 1));
		--tw-bg-opacity: 0.6;
		padding: 4px;
		opacity: 0;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}

	.gif-preview:hover .gif-preview__close {
		pointer-events: auto;
		cursor: pointer;
		opacity: 1;
}

	.gif-preview__close:hover {
		--tw-bg-opacity: 1;
}

	@media (max-width: 768px) {
		.input-section {
				flex-direction: column;
		}

		.comment-composer-submit-wrap {
				width: 100%;
		}

		.gif-preview__close {
				pointer-events: auto;
				opacity: 1;
		}
	}
</style>
